import { type KyInstance } from 'ky';

import { buildSearchParams, handleRequest } from '@/utils/httpUtils';

import type {
  Cancel,
  CancelResponse,
  Confirm,
  ConfirmResponse,
  ConfirmToday,
  ConfirmTodayResponse,
  CreateReport,
  CreateReportResponse,
  Labels,
  LabelsResponse,
  Map,
  MapResponse,
  OrderById,
  OrderByIdResponse,
  Orders,
  OrdersResponse,
  Pickups,
  PickupsResponse,
  Reports,
  ReportsResponse,
  Reschedule,
  RescheduleResponse,
  Statistics,
  StatisticsResponse,
} from './types';
import type {
  IReqCancel,
  IReqConfirm,
  IReqConfirmToday,
  IReqCreateReport,
  IReqLabels,
  IReqMap,
  IReqOrderById,
  IReqOrders,
  IReqPickups,
  IReqReports,
  IReqReschedule,
  IReqStatistics,
} from '@/types/parcel/orders';

export interface IRequestOrders {
  orders: Orders;
  pickups: Pickups;
  statistics: Statistics;
  labels: Labels;
  orderById: OrderById;
  map: Map;
  reports: Reports;
  createReport: CreateReport;
  confirmToday: ConfirmToday;
  confirm: Confirm;
  reschedule: Reschedule;
  cancel: Cancel;
}

export function reqOrders(http: KyInstance): IRequestOrders {
  const route = 'api/orders';
  return {
    orders: (ars: IReqOrders) =>
      handleRequest<OrdersResponse>(http, 'get', `${route}/orders`, {
        searchParams: buildSearchParams(ars),
      }),
    orderById: ({ trackingId }: IReqOrderById) =>
      handleRequest<OrderByIdResponse>(http, 'get', `${route}/${trackingId}`),
    map: ({ trackingId }: IReqMap) =>
      handleRequest<MapResponse>(http, 'get', `${route}/${trackingId}/map`),
    pickups: (ars: IReqPickups) =>
      handleRequest<PickupsResponse>(http, 'get', `${route}/pickups`, {
        searchParams: buildSearchParams(ars),
      }),
    statistics: (ars: IReqStatistics) => {
      const { startDate, endDate } = ars;
      if ((startDate && !endDate) || (!startDate && endDate)) {
        return Promise.reject('Invalid date range');
      }
      return handleRequest<StatisticsResponse>(http, 'get', `${route}/statistics/v2`, {
        searchParams: buildSearchParams({ startDate, endDate }),
      });
    },
    labels: (ars: IReqLabels) =>
      handleRequest<LabelsResponse>(http, 'post', `${route}/labels`, { json: ars }),
    reports: (args: IReqReports) =>
      handleRequest<ReportsResponse>(http, 'get', `${route}/reports`, {
        searchParams: buildSearchParams(args),
      }),
    createReport: (ars: IReqCreateReport) =>
      handleRequest<CreateReportResponse>(http, 'post', `${route}/reports`, { json: ars }),
    confirmToday: (ars: IReqConfirmToday) =>
      handleRequest<ConfirmTodayResponse>(
        http,
        'get',
        `${route}/confirm-today/${ars.deliveryType}`,
      ),
    confirm: (ars: IReqConfirm) =>
      handleRequest<ConfirmResponse>(http, 'patch', `${route}/${ars.trackingId}/confirm`, {
        json: { pickUpAfter: ars.pickUpAfter },
      }),
    reschedule: ({ trackingId, pickUpAfter }: IReqReschedule) =>
      handleRequest<RescheduleResponse>(http, 'patch', `${route}/${trackingId}/reschedule`, {
        json: { pickUpAfter },
      }),
    cancel: ({ trackingId, reason }: IReqCancel) =>
      handleRequest<CancelResponse>(http, 'delete', `${route}/${trackingId}/cancel`, {
        json: { reason },
      }),
  };
}
